import { useRef, useState, useEffect } from "react";
import { BasicPitch, OnCompleteCallback } from "@spotify/basic-pitch";
import * as tf from "@tensorflow/tfjs";

type Props = {
  onPush: (notes: number[]) => void;
};

export const useNotesMicrophoneDetect7 = ({ onPush }: Props) => {
  const [isListening, setIsListening] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const modelRef = useRef<BasicPitch | null>(null);

  const THRESHOLD = 0.8; // Порог детекции onsets
  const WINDOW_SIZE = 22050 * 0.5; // 0.5s окна
  const HOP_SIZE = WINDOW_SIZE / 2; // 50% overlap

  const audioBufferRef = useRef<Float32Array>(new Float32Array(0));
  const activeNotesRef = useRef<Set<number>>(new Set()); // для стабилизации

  const concatFloat32 = (a: Float32Array, b: Float32Array) => {
    const c = new Float32Array(a.length + b.length);
    c.set(a, 0);
    c.set(b, a.length);
    return c;
  };

  const startListening = async () => {
    if (isListening) return;
    setIsLoading(true);

    // ⚡ WebGL backend для ускорения
    await tf.setBackend("webgl");
    await tf.ready();

    // загружаем модель
    const model = new BasicPitch("/basic_pitch_model/model.json");
    modelRef.current = model;

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const audioContext = new AudioContext({ sampleRate: 22050 });

    const source = audioContext.createMediaStreamSource(stream);
    const processor = audioContext.createScriptProcessor(4096, 1, 1);
    processor.onaudioprocess = async (event) => {
      const input = event.inputBuffer.getChannelData(0);
      audioBufferRef.current = concatFloat32(audioBufferRef.current, input);
              
      while (audioBufferRef.current.length >= WINDOW_SIZE) {
        const window = audioBufferRef.current.slice(0, WINDOW_SIZE);
        audioBufferRef.current = audioBufferRef.current.slice(HOP_SIZE); // сдвигаем окно

        const onComplete: OnCompleteCallback = (frames, onsets, contours) => {
          // Снимаем статус загрузки при первом успешном инференсе
          

          const midiNotes: number[] = [];
          const startIdx = Math.floor(onsets.length / 2); // только новая половина окна

          for (let i = startIdx; i < onsets.length; i++) {
            onsets[i].forEach((val, noteIdx) => {
              if (val > THRESHOLD) midiNotes.push(noteIdx + 21);
            });
          }

          // стабилизация: убираем кратковременные прыжки
          const newActiveNotes = new Set<number>();
          midiNotes.forEach((n) => {
            if (activeNotesRef.current.has(n) || true) {
              newActiveNotes.add(n);
            }
          });
          activeNotesRef.current = newActiveNotes;

          onPush(Array.from(activeNotesRef.current));
        };

        // запускаем инференс
        await modelRef.current!.evaluateModel(window, onComplete, (progress) => {if (progress === 1) setIsLoading(false)});
      }
    };

    source.connect(processor);
    processor.connect(audioContext.destination);

    audioContextRef.current = audioContext;
    processorRef.current = processor;
    streamRef.current = stream;

    setIsListening(true);
  };

  const stopListening = () => {
    processorRef.current?.disconnect();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    audioContextRef.current?.close();

    audioBufferRef.current = new Float32Array(0);
    activeNotesRef.current.clear();
    processorRef.current = null;
    streamRef.current = null;
    audioContextRef.current = null;
    modelRef.current = null;

    setIsListening(false);
    setIsLoading(false);
  };

  useEffect(() => () => stopListening(), []);

  return { startListening, stopListening, isListening, isLoading };
};